import { useEffect, useState, useRef } from "react";
import { useParams, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { trpcClient } from "@/lib/trpc";
import { useCompanyConfig } from "@/hooks/useCompanyConfig";
import { Loader2, Printer, ArrowLeft } from "lucide-react";
import { formatMoney, formatDate } from "@/lib/utils";
import { Button } from "@/components/ui/button";

export default function ARReconciliationPrintPage() {
  const { reconciliationId } = useParams({ strict: false }) as { reconciliationId: string };
  const config = useCompanyConfig();
  const isLoaded = true;
  
  useEffect(() => {
    const styleId = "print-preview-hide-layout";
    const style = document.createElement("style");
    style.id = styleId;
    style.textContent = `
            .md\\:flex.fixed.inset-y-0, 
            .fixed.inset-0.md\\:hidden,
            .fixed.inset-y-0.left-0,
            .dock-nav,
            header.sticky { display: none !important; }
            main { padding: 0 !important; margin: 0 !important; width: 100% !important; max-width: none !important; }
            @media print {
              .no-print { display: none !important; }
              @page { size: A4 portrait; margin: 15mm; }
              body { background: white !important; margin: 0; padding: 0; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
              #print-container { padding: 0 !important; margin: 0 !important; box-shadow: none !important; width: 100% !important; min-height: 0 !important; }
              /* FB-4EJRF0: nền xám vùng xem trước + lề p-8 không được in ra thành viền xám quanh trang */
              *:has(#print-container), #print-container { background: white !important; }
              /* Khung ứng dụng (lề menu trái, cao 1 màn hình + cuộn trong) không được cắt / đẩy lệch bản in nhiều trang */
              *:has(#print-container) { padding-left: 0 !important; margin-left: 0 !important; height: auto !important; overflow: visible !important; }
            }
        `;
    document.head.appendChild(style);
    return () => { document.getElementById(styleId)?.remove(); };
  }, []);

  const { data: detailData, isLoading } = useQuery({
    queryKey: ["debtReconciliationDetail", reconciliationId],
    queryFn: async () => {
      return await trpcClient.ar.getDebtReconciliationById.query({ id: reconciliationId });
    },
    enabled: !!reconciliationId,
  });

  const reconciliation = detailData?.reconciliation;
  const customer = detailData?.customer || reconciliation?.customer;
  const ledgerItems: any[] = detailData?.ledger || [];

  const handlePrint = () => {
    window.print();
  };

  if (isLoading || !isLoaded) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600 mb-4" />
        <p className="text-slate-500">Đang tải dữ liệu in...</p>
      </div>
    );
  }

  if (!reconciliation) {
    return (
      <div className="p-8 text-center text-red-500">
        Không tìm thấy biên bản đối soát.
      </div>
    );
  }

  const c = config || { companyName: "", address1: "", taxCode: "", phone: "", email: "" };
  const d = new Date();
  
  return (
    <div className="bg-slate-100 min-h-screen print:bg-white print:min-h-0">
      <div className="no-print bg-white border-b px-6 py-4 flex items-center justify-between sticky top-0 z-50 shadow-sm">
        <div className="flex items-center gap-4">
          <Link to="/finance/ar">
            <Button variant="ghost" size="icon" className="h-8 w-8 rounded-full">
              <ArrowLeft className="w-4 h-4" />
            </Button>
          </Link>
          <div>
            <h1 className="text-lg font-semibold text-slate-900">Xem trước bản in</h1>
            <p className="text-xs text-slate-500">Khổ A4 • Dọc</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={handlePrint} className="bg-slate-900 text-white hover:bg-slate-800">
            <Printer className="w-4 h-4 mr-2" />
            In Biên Bản
          </Button>
        </div>
      </div>

      <div className="p-8 flex justify-center print:block print:p-0">
        <div id="print-container" className="bg-white w-[210mm] min-h-[297mm] p-[15mm] shadow-md print:w-full print:min-h-0 print:shadow-none text-[13px] leading-relaxed text-black font-serif">
          
          {/* Header */}
          <div className="flex justify-between items-start border-b-2 border-black pb-4 mb-6">
            <div>
              <h2 className="font-bold text-base uppercase text-slate-900">{c.companyName || "CÔNG TY CP IN VÀ BAO BÌ APV"}</h2>
              <p className="mt-1">Địa chỉ: {c.address1 || "KCN Tân Bình, TPHCM"}</p>
              <p>Mã số thuế: {c.taxCode || "0312345678"} | Điện thoại: {c.phone || "028.3815.1234"}</p>
            </div>
          </div>

          {/* Title */}
          <div className="text-center mb-8">
            <h1 className="text-xl font-bold uppercase tracking-wide">BIÊN BẢN ĐỐI CHIẾU VÀ XÁC NHẬN CÔNG NỢ</h1>
            <p className="mt-2 font-medium">Kỳ đối soát: Tháng {reconciliation.month} Năm {reconciliation.year}</p>
            <p className="text-sm italic mt-1">(Từ ngày 01/{String(reconciliation.month).padStart(2, '0')}/{reconciliation.year} đến hết ngày {new Date(reconciliation.year, reconciliation.month, 0).getDate()}/{String(reconciliation.month).padStart(2, '0')}/{reconciliation.year})</p>
          </div>

          {/* Parties */}
          <div className="mb-6 space-y-4">
            <p>
              Hôm nay, ngày {String(d.getDate()).padStart(2, '0')} tháng {String(d.getMonth() + 1).padStart(2, '0')} năm {d.getFullYear()}, chúng tôi gồm có:
            </p>
            
            <div>
              <p className="font-bold uppercase">Bên A: {c.companyName || "CÔNG TY CP IN VÀ BAO BÌ APV"}</p>
              <p className="pl-4">Đại diện: Ông/Bà .............................................................. Chức vụ: Kế toán trưởng</p>
              <p className="pl-4">Địa chỉ: {c.address1 || "KCN Tân Bình, TPHCM"}</p>
            </div>
            
            <div>
              <p className="font-bold uppercase">Bên B: {reconciliation.customer?.name || ".............................................................."}</p>
              <p className="pl-4">Đại diện: Ông/Bà .............................................................. Chức vụ: ...........................................</p>
              <p className="pl-4">Địa chỉ: {reconciliation.customer?.address || "................................................................................................"}</p>
              <p className="pl-4">Điện thoại: {reconciliation.customer?.phone || "......................................."} | Mã số thuế: {reconciliation.customer?.taxCode || "......................................."}</p>
            </div>

            <p className="pt-2 font-medium">Cùng nhau đối chiếu xác nhận công nợ với các nội dung sau:</p>
          </div>

          {/* Summary Table */}
          <table className="w-full border-collapse border border-black mb-6">
            <tbody>
              <tr>
                <td className="border border-black p-2 font-medium w-2/3">1. Số dư nợ đầu kỳ:</td>
                <td className="border border-black p-2 font-bold text-right w-1/3">{formatMoney(Number(reconciliation.openingBalance || 0))} đ</td>
              </tr>
              <tr>
                <td className="border border-black p-2 font-medium">2. Số tiền phát sinh tăng (Giao hàng):</td>
                <td className="border border-black p-2 font-bold text-right">{formatMoney(Number(reconciliation.totalIncurred || 0))} đ</td>
              </tr>
              <tr>
                <td className="border border-black p-2 font-medium">3. Số tiền phát sinh giảm (Đã thu):</td>
                <td className="border border-black p-2 font-bold text-right">{formatMoney(Number(reconciliation.totalPaid || 0))} đ</td>
              </tr>
              <tr>
                <td className="border border-black p-2 font-bold bg-slate-100">4. Số dư nợ cuối kỳ (Còn phải thu):</td>
                <td className="border border-black p-2 font-bold text-right bg-slate-100 text-base">{formatMoney(Number(reconciliation.closingBalance || 0))} đ</td>
              </tr>
            </tbody>
          </table>

          {/* Details Table */}
          {ledgerItems.length > 0 && (
            <div className="mb-8 mt-8">
              <h3 className="font-bold mb-2">Chi tiết phát sinh trong kỳ:</h3>
              <table className="w-full border-collapse border border-black text-xs">
                <thead>
                  <tr className="bg-slate-100">
                    <th className="border border-black p-1.5 text-center w-[40px]">STT</th>
                    <th className="border border-black p-1.5 text-center w-[80px]">Ngày</th>
                    <th className="border border-black p-1.5 text-center w-[120px]">Số chứng từ</th>
                    <th className="border border-black p-1.5 text-left">Diễn giải</th>
                    <th className="border border-black p-1.5 text-right w-[100px]">Phát sinh Nợ</th>
                    <th className="border border-black p-1.5 text-right w-[100px]">Phát sinh Có</th>
                  </tr>
                </thead>
                <tbody>
                  {ledgerItems.map((item: any, idx: number) => {
                    const isDebit = item.type === 'INVOICE' || item.type === 'DELIVERY' || (item.type === 'ADJUSTMENT' && item.amount > 0);
                    const isCredit = item.type === 'RECEIPT' || item.type === 'PAYMENT' || (item.type === 'ADJUSTMENT' && item.amount < 0);
                    const absAmt = Math.abs(Number(item.amount || item.debit || item.credit || 0));
                    
                    return (
                      <tr key={idx}>
                        <td className="border border-black p-1.5 text-center">{idx + 1}</td>
                        <td className="border border-black p-1.5 text-center">{formatDate(item.date)}</td>
                        <td className="border border-black p-1.5 text-center font-medium">{item.refNo || item.refId || '---'}</td>
                        <td className="border border-black p-1.5">{item.description}</td>
                        <td className="border border-black p-1.5 text-right">{(isDebit || item.debit > 0) ? formatMoney(absAmt || item.debit) : ""}</td>
                        <td className="border border-black p-1.5 text-right">{(isCredit || item.credit > 0) ? formatMoney(absAmt || item.credit) : ""}</td>
                      </tr>
                    );
                  })}
                  {!ledgerItems.length && (
                    <tr>
                      <td colSpan={6} className="border border-black p-4 text-center italic text-slate-500">
                        Không có phát sinh trong kỳ
                      </td>
                    </tr>
                  )}
                  {ledgerItems.length > 0 && (
                    <tr className="font-bold bg-slate-50">
                      <td colSpan={4} className="border border-black p-1.5 text-right">Tổng cộng phát sinh:</td>
                      <td className="border border-black p-1.5 text-right">{formatMoney(Number(reconciliation.totalIncurred || 0))}</td>
                      <td className="border border-black p-1.5 text-right">{formatMoney(Number(reconciliation.totalPaid || 0))}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* Conclusion */}
          <div className="mb-12">
            <p>Tính đến hết ngày {new Date(reconciliation.year, reconciliation.month, 0).getDate()}/{String(reconciliation.month).padStart(2, '0')}/{reconciliation.year}, Công ty {reconciliation.customer?.name} còn nợ Công ty CP In và Bao bì APV số tiền là: <span className="font-bold text-base">{formatMoney(Number(reconciliation.closingBalance || 0))} VNĐ</span></p>
            <p className="mt-2 italic">Biên bản được lập thành 02 bản, mỗi bên giữ 01 bản có giá trị pháp lý như nhau. Đề nghị Quý khách hàng kiểm tra, xác nhận và gửi lại 01 bản cho chúng tôi.</p>
          </div>

          {/* Signatures */}
          <div className="flex justify-between mt-8 pt-4">
            <div className="text-center w-1/2">
              <p className="font-bold uppercase">XÁC NHẬN CỦA BÊN B</p>
              <p className="italic text-sm">(Ký, ghi rõ họ tên và đóng dấu)</p>
              <div className="h-32"></div>
            </div>
            <div className="text-center w-1/2">
              <p className="font-bold uppercase">XÁC NHẬN CỦA BÊN A</p>
              <p className="italic text-sm">(Ký, ghi rõ họ tên và đóng dấu)</p>
              <div className="h-32"></div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}
